import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import {
    Button,
    Card,
    CardBody,
    Col,
    FormFeedback,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
} from "reactstrap"
import toast, { Toaster } from "react-hot-toast"
import useApi from "common/ApiMiddlewere"
import { useLocation } from "react-router-dom"
import breadcrumbs from "common/Breadcrumbs"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import DailyAttendTable from "./DailyAttendTable"
import Flatpickr from "react-flatpickr";
import { dateTimeFormat, formatDateToISO } from "pages/Common"
import TransactionTable from "pages/Master/Vendors/TransactionTable"
import { t } from "i18next"

const colourStyles = {
    option: (styles, { isDisabled, isFocused }) => {
        return {
            ...styles,
            backgroundColor: isFocused ? "#556ee6" : "#f8f9fa",
            color: isFocused ? "#fff" : "#495057",
            cursor: isDisabled ? "not-allowed" : "default",
            padding: "3px 10px",
        }
    },
    control: (styles, { isDisabled }) => {
        return {
            ...styles,
            border: "1px solid #ced4da",
            minHeight: "35px",
            padding: "3px 10px",
            fontSize: "12px",
            backgroundColor: isDisabled ? "#e9ecef" : "#fff",
            color: isDisabled ? "#6c757d" : "#495057",
            cursor: isDisabled ? "not-allowed" : "default",
            pointerEvents: isDisabled ? "none" : "auto",
            opacity: isDisabled ? 0.6 : 1,
        };
    },
}

function AttendanceDailyReport() {
    document.title = `Daily Attendance Report | Raja Rani Couture`
    let { pathname } = useLocation()
    const { deleteApi, getApi, postApi, putApi } = useApi()
    const [userFilter, setUserFilter] = useState(0)
    const [users, setUsers] = useState([])
    const [date, setDate] = useState(new Date())
    const [screenLoad, setScreenLoad] = useState(false)
    const [tableData, setTableData] = useState([])
    const [columns, setColumns] = useState([]);
    const [totalRecords, settoTalRecords] = useState(0);
    const [attendanceLogs, setAttendanceLogs] = useState([])
    const [totalHours, setTotalHours] = useState('0:00')
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 10))
    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [todayPresent, setTodayPresent] = useState(0);
    const [todayAbsent, setTodayAbsent] = useState(0);
    const [attendanceValue, setAttendanceValue] = useState('')
    const [overTime, setOverTime] = useState('00:00')
    const [isOverTimeRequired, setIsOverTimeRequired] = useState(false)
    const [isOverTimeRequiredMessage, setIsOverTimeRequiredMessage] = useState('')
    const [loading, setLoading] = useState(false)
    const [userId, setUserId] = useState(0)

    useEffect(() => {
        getApi('Attendance/user-lookup').then(({ data, status }) => {
            if (status === 200) {
                setUsers(data)
            } else {
                toast.error(data, {
                    style: {
                        fontSize: "15px",
                    },
                })
            }
        })
    }, [])

    const dailyAttendanceReport = () => {
        setScreenLoad(true);
        postApi("Attendance/daily-report", {
            AttendanceUserId: (JSON.parse(localStorage.getItem("authUser")).RoleName == "Admin" || JSON.parse(localStorage.getItem("authUser")).RoleName == "Master") ? parseInt(userFilter) : JSON.parse(localStorage?.getItem("authUser")).AttendanceUserId,
            Date: month ? month : new Date().toISOString().slice(0, 10),
        }).then(({ data, status }) => {
            if (status === 200) {
                if (data && data.length > 0) {
                    const pivotData = transformToPivot(data);
                    settoTalRecords(pivotData.length);

                    if (pivotData.length > 0) {
                        const dynamicCols = createPivotColumns(pivotData, month);
                        setColumns(dynamicCols);
                        setTableData(pivotData);
                        const summary = getTodayAttendanceSummary(pivotData);
                        setTodayPresent(summary.present);
                        setTodayAbsent(summary.absent);
                    } else {
                        setColumns([]);
                        setTableData([]);
                        settoTalRecords(0);
                    }
                } else {
                    setColumns([]);
                    setTableData([]);
                    settoTalRecords(0);
                }
            } else {
                toast.error(data, { style: { fontSize: "15px" } });
            }
            setScreenLoad(false);
        });
    };

    const getTodayAttendanceSummary = (data) => {
        if (!data || data.length === 0) return { present: 0, absent: 0 };

        const today = new Date();
        const day = today.getDate().toString();

        let presentCount = 0;
        let absentCount = 0;

        data.forEach(emp => {
            const status = emp[day];
            if (status === "P" || status === "M") presentCount++;
            else if (status === "A") absentCount++;
        });

        return { present: presentCount, absent: absentCount };
    };

    const transformToPivot = (data) => {
        if (!data || data.length === 0) return [];

        const pivotMap = new Map();
        const allDays = new Set();

        data.forEach(record => {
            const empId = record.Empcardno;
            const name = record.Name;
            const date = new Date(record.Date);
            const day = date.getDate();
            const status = record.Status?.charAt(0)?.toUpperCase() || "-";
            const overTime = record.OverTime || "00:00"; // ✅ OT value

            allDays.add(day);

            if (!pivotMap.has(empId)) {
                pivotMap.set(empId, {
                    Name: name,
                    Empcardno: empId
                });
            }

            const employeeRecord = pivotMap.get(empId);

            // ✅ Attendance (P / A / M)
            employeeRecord[day] = status;

            // ✅ Store overtime for that day
            employeeRecord[`OT_${day}`] = overTime;
        });

        const sortedDays = Array.from(allDays).sort((a, b) => a - b);
        const result = Array.from(pivotMap.values());

        // ✅ Fill missing days with "-"
        result.forEach(employee => {
            sortedDays.forEach(day => {
                if (!employee.hasOwnProperty(day)) {
                    employee[day] = "-";
                }
                if (!employee.hasOwnProperty(`OT_${day}`)) {
                    employee[`OT_${day}`] = "00:00";
                }
            });
        });

        return result;
    };

    // Handle cell click to open modal
    const handleCellClick = (empCardNo, date, presentValue, overtimeValue) => {
        setUserId(parseInt(empCardNo))
        setAttendanceValue(presentValue)
        setOverTime(overtimeValue)
        setModalOpen(true)
        postApi("Attendance/emp-attendance-list", {
            fromDate: formatDateToISO(date),
            toDate: formatDateToISO(date),
            AttendanceUserId: empCardNo,
            displayStart: 1,
            pageSize: 100,
            searchText: '',
            sortColumn: "",
            sortOrder: "",
        }).then(({ data, status }) => {
            if (status === 200) {
                setAttendanceLogs(data?.EmpAttendanceLogList)
                setTotalHours(data?.TotalHours || '0:00')
            } else {
                toast.error(data, {
                    style: {
                        fontSize: "15px",
                    },
                })
            }
            setScreenLoad(false)
        })
    };

    // Create columns with click handlers
    const createPivotColumns = (pivotData, selectedDate) => {
        const baseColumns = [
            {
                Header: "Name",
                Header: () => (
                    <div
                        style={{
                            fontWeight: "bold",
                            fontSize: "18px"
                        }}
                    >
                        Name
                    </div>
                ),
                accessor: "Name",
                width: "5%",
                Cell: ({ value }) => (
                    <div style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: "start",
                        fontSize: "17px",
                        fontWeight: "bold"
                    }}>
                        {value}
                    </div>
                )
            }
        ];
        const dateObj = new Date(selectedDate);
        const year = dateObj.getFullYear();
        const month = dateObj.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const dayColumns = Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => ({
            Header: (
                <div style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "18px"
                }}>
                    {day}
                </div>
            ),
            accessor: day.toString(),
            width: "50px",
            Cell: ({ row, value }) => {
                const fullDate = new Date(year, month, day);
                return (
                    <div
                        style={{
                            textAlign: "center",
                            color: value === "P" ? "#4CAF50" : value === "A" ? "#F44336" : "#6060ff",
                            fontSize: "17px",
                            fontWeight: "bold",
                            cursor: value === "A" ? "default" : "pointer",
                            borderRadius: "3px",
                        }}
                        title={value === "P" ? "Present - Click for details" :
                            value === "A" ? "Absent" : "Miss Punch - Click for details"}
                        onClick={() => {
                            if (value !== "A") {
                                const otKey = `OT_${day}`;
                                const overtimeValue = row.original[otKey] || "00:00";
                                handleCellClick(
                                    row.original.Empcardno,
                                    fullDate,
                                    value,            // P / M
                                    overtimeValue     // ✅ pass OT here
                                );
                            }
                        }}
                    >
                        {value}
                    </div>
                )
            }
        }));

        return [...baseColumns, ...dayColumns];
    };

    useEffect(() => {
        dailyAttendanceReport()
    }, [userFilter, month])

    const attendanceColumns = useMemo(
        () => [
            {
                Header: "PunchIn Date-Time",
                accessor: "PunchInDateTime",
                width: "15%",
                Cell: ({ row }) => (
                    <p className=" mb-0">
                        {row.original?.PunchInDateTime == "0001-01-01T00:00:00"
                            ? "N/A"
                            : dateTimeFormat(row.original?.PunchInDateTime, "dd-MM-yyyy HH:mm tt")}
                    </p>
                ),
            },
            {
                Header: "PunchOut Date-Time",
                accessor: "PunchOutDateTime",
                width: "15%",
                Cell: ({ row }) => (
                    <p className=" mb-0">
                        {row.original?.PunchOutDateTime
                            ? dateTimeFormat(row.original?.PunchOutDateTime, "dd-MM-yyyy HH:mm tt")
                            : "N/A"}
                    </p>
                ),
            },
            {
                Header: "Hours",
                accessor: "TotalTime",
                width: "9%",
            },
        ],
        [attendanceLogs]
    )

    const handleKeyPress = (event) => {
        const charCode = event.which || event.keyCode;

        // Allow numbers (48–57) and colon (: = 58)
        if (
            (charCode < 48 || charCode > 57) && // not 0-9
            charCode !== 58 // allow :
        ) {
            event.preventDefault();
        }
    };

    const handleOverTimeChange = (value) => {
        setIsOverTimeRequired(false);
        setOverTime(value);
    };

    const handleSubmit = () => {
        const timeRegex = /^([0-9]{2}):([0-5][0-9])$/;
        if (overTime.trim() === '') {
            setIsOverTimeRequired(true);
            setIsOverTimeRequiredMessage("overTime is Required.");
        }
        else if (!timeRegex.test(overTime)) {
            setIsOverTimeRequired(true);
            setIsOverTimeRequiredMessage("Invalid time format. Please use HH:MM.");
        } else {
            setLoading(true);
            setIsOverTimeRequiredMessage('')
            setIsOverTimeRequired(false);
            postApi("Attendance/add-over-time", {
                UserId: userId,
                OverTime: overTime,
                Date: (attendanceLogs[0]?.PunchInDateTime)?.split("T")[0],
            }).then(({ data, status }) => {
                if (status === 200) {
                    setLoading(false);
                    setModalOpen(false);
                    dailyAttendanceReport()
                    toast.success("Successfully Added Over-Time", {
                        style: {
                            fontSize: "15px",
                        },
                    })
                } else {
                    setLoading(false);
                    toast.error(data, {
                        style: {
                            fontSize: "15px",
                        },
                    })
                }
            })

        }
    }

    return (
        <div className="page-content">
            <div className="container-fluid">
                {screenLoad && (
                    <div className="loading-overlay is-active">
                        <span className="fas fa-spinner fa-3x fa-spin"></span>
                    </div>
                )}

                <Breadcrumbs
                    title={breadcrumbs[pathname].title}
                    breadcrumbItem={breadcrumbs[pathname].breadcrumbItem}
                />
                <Card>
                    <CardBody>
                        <Row>
                            {(JSON.parse(localStorage.getItem("authUser")).RoleName == "Admin" || JSON.parse(localStorage.getItem("authUser")).RoleName == "Master") &&
                                <Col className="col-3 mb-3">
                                    <Input
                                        type="select"
                                        id="validationCustom01"
                                        className="form-control"
                                        name="taskType"
                                        onChange={e => {
                                            setUserFilter(e.target.value)
                                        }}
                                        value={userFilter}
                                    >
                                        <option value={0}>Select User</option>
                                        {users.map((item, index) => {
                                            return (
                                                <option key={index} value={item.Key}>
                                                    {item.Value}
                                                </option>
                                            )
                                        })}
                                    </Input>
                                </Col>
                            }
                            <Col className="col-3 mb-3">
                                <Input
                                    name="form"
                                    type="month"
                                    dateFormat="M/YYYY"
                                    className="form-control"
                                    max={new Date().toISOString().slice(0, 7)}
                                    defaultValue={new Date().toISOString().slice(0, 7)}
                                    onChange={e => {
                                        const [year, month] = e.target.value.split("-");
                                        const numericYear = parseInt(year, 10);
                                        if (numericYear > 2024) {
                                            const formattedDate = `${numericYear}-${month}-01`;
                                            setMonth(formattedDate);
                                        }
                                    }}
                                />
                            </Col>
                            <Col md={(JSON.parse(localStorage.getItem("authUser")).RoleName == "Admin" || JSON.parse(localStorage.getItem("authUser")).RoleName == "Master") ? 6 : 9} style={{ fontSize: "14px" }}>
                                <div style={{ display: "flex", gap: "15px", justifyContent: "end" }}>
                                    <div style={{ display: "flex", gap: "5px" }}>
                                        <div style={{ width: '12px', height: '12px', backgroundColor: "#4CAF50", marginTop: "3px", border: `1px solid #4CAF50` }}>
                                        </div>
                                        <div>Present </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "5px" }}>
                                        <div style={{ width: '12px', height: '12px', backgroundColor: "#F44336", marginTop: "3px", border: `1px solid #F44336` }}>
                                        </div>
                                        <div>Absent </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "5px" }}>
                                        <div style={{ width: '12px', height: '12px', backgroundColor: "#6060ff", marginTop: "3px", border: `1px solid #6060ff` }}>
                                        </div>
                                        <div>Miss Punch</div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: "15px", justifyContent: "end" }}>
                                    <div style={{ display: "flex", gap: "5px", fontWeight: "bold" }}>
                                        <div>Today's Present: {todayPresent}</div>
                                    </div>
                                    <div style={{ display: "flex", gap: "5px", fontWeight: "bold" }}>
                                        <div>Today's Absent: {todayAbsent} </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col className="col-12">
                                <DailyAttendTable
                                    columns={columns}
                                    data={tableData}
                                    customPageSize={10}
                                    className="custom-header-css"
                                    TotalRecords={totalRecords}
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
                <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered={true} size="defualt">
                    <div className="modal-content">
                        <ModalHeader className="text-centre" toggle={() => setModalOpen(!modalOpen)} tag="h5">
                            Attendance Logs
                        </ModalHeader>
                        <ModalBody>
                            <div style={{ textAlign: "end", fontSize: "14px" }}>Total Hours: {totalHours}</div>
                            <TransactionTable
                                columns={attendanceColumns}
                                data={attendanceLogs}
                            />
                            {attendanceValue == "P" &&
                                <Row>
                                    <Col md={7} style={{ paddingRight: "0px" }}>
                                        <FormGroup style={{ display: "flex", justifyContent: "start", alignItems: "center", gap: "10px" }}>
                                            <div className="form-label" >Over Time(Hours):</div>
                                            <div>
                                                <Input
                                                    type="text"
                                                    id="overTime"
                                                    placeholder="HH:MM"
                                                    className="form-control form-control-sm"
                                                    name="overTime"
                                                    bsSize="sm"
                                                    onKeyPress={handleKeyPress}
                                                    value={overTime}
                                                    onChange={e => handleOverTimeChange(e.target.value)}
                                                    invalid={isOverTimeRequired}
                                                />
                                                {isOverTimeRequired &&
                                                    <FormFeedback type="invalid">
                                                        {isOverTimeRequiredMessage}
                                                    </FormFeedback>
                                                }
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3} style={{ paddingLeft: "0px" }}>
                                        <div className="text-start">
                                            {loading ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    color="secondary"
                                                >
                                                    <i className="bx bx-hourglass bx-spin font-size-16 align-middle me-2"></i>
                                                    Loading
                                                </button>
                                            ) : (
                                                <>

                                                    <Button
                                                        user="primary"
                                                        className="btn btn-primary btn-sm "
                                                        type="button"
                                                        color="primary"
                                                        onClick={() => handleSubmit()}
                                                    >
                                                        Submit
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            }
                        </ModalBody>
                    </div>
                </Modal >

            </div >
        </div >
    )
}

AttendanceDailyReport.propTypes = {
    preGlobalFilteredRows: PropTypes.any,
    onFilterChange: PropTypes.func,
}

export default AttendanceDailyReport